import { useEffect, useState } from 'react';
import { BridgeClient } from '../../../app/service/bridge/BridgeClient';
import { normalizeExperienceType } from '../experienceTypes';

/**
 * Maps the highest achieved onboarding checkpoint to the wizard step index
 * the user should resume from.
 *
 * Step indices (0-based) match WIZARD_CONFIG.steps order:
 *   0 – experience-selection  (welcome)
 *   1 – choose-experience
 *   2 – component-configuration
 *   3 – theme-configuration
 *   4 – summary
 *
 * Checkpoint priority (highest precedence first):
 *   preview_built              → resume at 4 (summary)
 *   integration_step_completed → resume at 3 (theme-configuration)
 *   mode_selected              → resume at 2 (component-configuration)
 *   (none)                     → resume at 0 (fresh start / welcome)
 */
export function computeResumeStepIndex(onboarding) {
  if (!onboarding) return 0;
  if (onboarding.preview_built) return 4;
  if (onboarding.integration_step_completed) return 3;
  if (onboarding.mode_selected) return 2;
  return 0;
}

const FRESH_START = {
  isLoading: false,
  isCompleted: false,
  initialStepIndex: 0,
  initialWizardData: {},
};

/**
 * useWizardResume — reads plugin-owned onboarding state at mount and returns
 * the step index and wizard data the wizard should open to.
 *
 * @param {Object|null} [bridgeClient] - Optional BridgeClient instance.  When
 *   provided (e.g. from ShellAppContextProvider via useShellAppContext) the
 *   existing instance is reused instead of creating a new one.  Falls back to
 *   `new BridgeClient()` when null/undefined — keeps backward compatibility
 *   for tests and non-shell render paths.
 *
 * Behaviour:
 * - isLoading: true until the bridge responds.  Callers should show a loading
 *   state and not render any step content yet, preventing a visual jump.
 * - On bridge error: silently falls back to fresh start (step 0, no data).
 * - When onboarding is already completed: returns isCompleted=true so the
 *   caller can exit the flow immediately without re-showing the wizard.
 * - If the bridge response contains onboarding.selected_experience it is
 *   included in initialWizardData and resume proceeds to the checkpoint step.
 *   If selected_experience is absent, resume past choose-experience (step 1)
 *   is capped at step 1 to avoid landing on a broken configuration or theme
 *   step with no experience selected.
 */
export function useWizardResume(bridgeClient, { enabled = true } = {}) {
  const [state, setState] = useState({
    isLoading: true,
    isCompleted: false,
    initialStepIndex: 0,
    initialWizardData: {},
  });

  useEffect(() => {
    if (!enabled) {
      setState(FRESH_START);
      return undefined;
    }

    let cancelled = false;
    const bridge = bridgeClient || new BridgeClient();

    bridge
      .sendMessageWithResponse('onboarding.state', {})
      .then((response) => {
        if (cancelled) return;

        const onboardingState = response?.state || response;
        const onboarding = onboardingState?.onboarding;

        // Wizard was completed, but preview was subsequently invalidated
        // (e.g. operator changed interface settings).  Re-open at Step 5 so
        // a fresh bundle build can be triggered instead of redirecting home.
        // typeof guard: old plugins that do not emit preview_built at all must
        // not re-enter the wizard — they should still reach isCompleted=true.
        if (onboarding?.wizard_completed
            && typeof onboarding?.preview_built === 'boolean'
            && !onboarding.preview_built) {
          const selectedExperience = normalizeExperienceType(onboarding?.selected_experience);
          setState({
            isLoading: false,
            isCompleted: false,
            initialStepIndex: 4,
            initialWizardData: selectedExperience ? { selectedExperience } : {},
          });
          return;
        }

        if (onboarding?.wizard_completed) {
          setState({
            isLoading: false,
            isCompleted: true,
            initialStepIndex: 0,
            initialWizardData: {},
          });
          return;
        }

        const rawStepIndex = computeResumeStepIndex(onboarding);
        const selectedExperience = normalizeExperienceType(onboarding?.selected_experience);

        // Cap resume past choose-experience (step 1) when selected_experience
        // is absent — prevents landing on a broken configuration or theme step.
        const initialStepIndex = (rawStepIndex > 1 && !selectedExperience)
          ? 1
          : rawStepIndex;

        setState({
          isLoading: false,
          isCompleted: false,
          initialStepIndex,
          initialWizardData: selectedExperience ? { selectedExperience } : {},
        });
      })
      .catch(() => {
        if (cancelled) return;
        // Bridge unavailable — silent fallback, wizard starts fresh
        setState(FRESH_START);
      });

    return () => {
      cancelled = true;
    };
  }, [enabled]);

  return state;
}
